import React from "react";
import {Container, Button} from "reactstrap";
import {useNavigate} from "react-router-dom";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";

interface AuthRequiredViewProps {
  title?: string;
  subtitle?: string;
  features?: Array<{
    icon: string;
    title: string;
    description: string;
    items: string[];
  }>;
}

const AuthRequiredView: React.FC<AuthRequiredViewProps> = ({
  title = "Ready to Analyze Your Virtual Cycling Set-up?",
  subtitle = "Sign in to access this feature and get instant expert-level diagnostics",
  features = [
    {
      icon: "fa-network-wired",
      title: "Network Analysis",
      description: "Detect connection issues causing riders to disappear or teleport",
      items: ["TCP/UDP error detection", "Packet loss monitoring", "Firewall issue identification"]
    },
    {
      icon: "fa-desktop",
      title: "Graphics Performance",
      description: "Identify FPS drops, CPU bottlenecks, and configuration problems",
      items: ["Frame rate analysis", "CPU bottleneck detection", "GPU optimization tips"]
    },
    {
      icon: "fa-heartbeat",
      title: "Sensor Connectivity",
      description: "Monitor ANT+ and Bluetooth reliability for power and heart rate",
      items: ["Dropout detection", "Battery pattern analysis", "Interference identification"]
    }
  ]
}) => {
  const navigate = useNavigate();

  return (
    <BackgroundColorContext.Consumer>
      {() => (
        <div className="upload-marketing-section">
          <Container>
            <div className="marketing-hero-mini">
              <div className="hero-content-mini">
                <h2>{title}</h2>
                <p>{subtitle}</p>
                <div className="cta-buttons">
                  <Button
                    color="secondary"
                    size="lg"
                    outline
                    onClick={() => navigate("/auth/signin")}
                    style={{borderColor: "rgba(255,255,255,0.7)", color: "#fff"}}
                  >
                    <i className="fa fa-sign-in-alt mr-2"></i>
                    Sign In
                  </Button>
                </div>
              </div>
            </div>

            <div className="quick-stats">
              <h3>What You'll Get</h3>
              <div className="stats-row">
                <div className="stat-item">
                  <div className="stat-value">5</div>
                  <div className="stat-label">Free analyses per month</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">43</div>
                  <div className="stat-label">Analysis rules</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">67</div>
                  <div className="stat-label">Expert recommendations</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">5</div>
                  <div className="stat-label">Performance categories</div>
                </div>
              </div>
            </div>

            <div className="benefits-grid">
              {features.map((feature, index) => (
                <div key={index} className="benefit-card">
                  <div className="benefit-icon">
                    <i className={`fa ${feature.icon}`}></i>
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                  <ul>
                    {feature.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="how-it-works-mini">
              <h3>How It Works</h3>
              <div className="steps-grid">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <h4>Sign In</h4>
                  <p>Use your existing TrainerDX account</p>
                </div>
                <div className="step-item">
                  <div className="step-number">2</div>
                  <h4>Upload Log</h4>
                  <p>Drag and drop your Virtual Cycling log file (Zwift supported, other platforms on our roadmap)</p>
                </div>
                <div className="step-item">
                  <div className="step-number">3</div>
                  <h4>Get Analysis</h4>
                  <p>Instant comprehensive report with issues</p>
                </div>
                <div className="step-item">
                  <div className="step-number">4</div>
                  <h4>Apply Fixes</h4>
                  <p>Follow step-by-step recommendations</p>
                </div>
              </div>
            </div>
          </Container>
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default AuthRequiredView;
